<template>
    <div class="sm:px-3 py-1">
        <div class="flex gap-3 items-center pb-4 border-b border-neutral-100">
            <div class="flex justify-center items-center p-2 rounded-full bg-primary_blue bg-opacity-10">
                <Icons.TIInvoice class="h-5 sm:h-7 w-5 sm:w-7 fill-primary_blue" />
            </div>
            <div class="mb-1">
                <p class="text-sm sm:text-base text-neutral-800">
                    {{ props.ride ? t('taxilib.receipt.ride') : t('taxilib.receipt.title') }}
                </p>
                <p class="text-xs text-neutral-500 -mt-[3px]">
                    {{ props.ride ? t('taxilib.receipt.ride_desc') : t('taxilib.receipt.title_desc') }}
                </p>
            </div>
        </div>
        <div class="space-y-2 sm:space-y-3 py-4 border-b border-neutral-100">
            <HqDetailElement
                :icon="Icons.TIAppointment"
                :title="t('taxilib.receipt.start')"
                :value="receipt.start && display_utc_local(receipt.start)">
            </HqDetailElement>
            <HqDetailElement
                :icon="Icons.TIPickup"
                :title="t('taxilib.receipt.start_address')">
                <template #value>
                    <div v-if="receipt.start_city || receipt.start_address">
                        <p v-if="receipt.start_city">{{ receipt.start_city }}</p>
                        <p v-if="receipt.start_address">{{ receipt.start_address }}</p>
                    </div>
                    <div v-else>
                        &ndash;
                    </div>
                </template>
            </HqDetailElement>
            <HqDetailElement
                :icon="Icons.TIAppointment"
                :title="t('taxilib.receipt.finish')"
                :value="receipt.finish && display_utc_local(receipt.finish)"
            />
            <HqDetailElement
                :icon="Icons.TIDestination"
                :title="t('taxilib.receipt.finish_address')">
                <template #value>
                    <div v-if="receipt.finish_city || receipt.finish_address">
                        <p v-if="receipt.finish_city">{{ receipt.finish_city }}</p>
                        <p v-if="receipt.finish_address">{{ receipt.finish_address }}</p>
                    </div>
                    <div v-else>
                        &ndash;
                    </div>
                </template>
            </HqDetailElement>
            <HqDetailElement
                v-if="receipt.distance"
                :icon="Icons.TIRoute"
                :title="t('taxilib.receipt.distance')"
                :value="`${meters_to_kilometers(receipt.distance)} ${t('common.kilometers')}`"
            />
        </div>
        <div class="space-y-2 sm:space-y-3 py-4 border-b border-neutral-100">
            <HqDetailElement
                v-if="receipt.price"
                :icon="Icons.TICoins"
                :title="t('taxilib.receipt.price')"
                :value="`${receipt.price} zł`"
            />
            <HqDetailElement
                v-if="receipt.payment_method"
                :icon="Icons.TIPayment"
                :title="t('taxilib.receipt.payment_method')"
                :value="t(`taxilib.receipt.payment_method_value.${receipt.payment_method}`)"
            />
            <HqDetailElement
                :icon="Icons.TINote"
                :title="t('taxilib.receipt.client_vatid')"
                :value="receipt.client_vatid"
            />
        </div>
        <div class="space-y-2 sm:space-y-3 py-4 border-b border-neutral-100">
            <HqDetailElement
                v-if="receipt.instance_id"
                :icon="Icons.TIDocs"
                :title="t('taxilib.receipt.instance_id')"
                :value="receipt.instance_taxi_code"
            />
            <HqDetailElement
                :title="t('taxilib.receipt.ecr_status')"
                :value="receipt.ecr_status ? t(`taxilib.receipt.ecr_status_value.${receipt.ecr_status}`) : null"
            />
            <HqDetailElement
                v-if="!props.ride"
                :icon="Icons.TILocateMe"
                :title="t('taxilib.receipt.responded_at')"
                :value="receipt.responded_at && display_utc_local(receipt.responded_at)"
            />
        </div>
        <div class="space-y-2 sm:space-y-3 py-4 border-b border-neutral-100">
            <HqDetailElement
                :icon="Icons.TIPerson"
                :title="t('taxilib.receipt.driver_fullname')"
                :value="receipt.driver_fullname"
            />
            <HqDetailElement
                v-if="!props.ride"
                :icon="Icons.TIPerson"
                :title="t('taxilib.receipt.driver_username')"
                :value="receipt.driver_username"
            />
            <HqDetailElement
                v-if="!props.ride"
                :icon="Icons.TIPerson"
                :title="t('taxilib.receipt.driver_medical_examination_to')"
                :value="receipt.driver_medical_examination_to"
            />
            <HqDetailElement
                v-if="!props.ride"
                :icon="Icons.TIPerson"
                :title="t('taxilib.receipt.driver_psychotest_to')"
                :value="receipt.driver_psychotest_to"
            />
        </div>
        <div class="space-y-2 sm:space-y-3 py-4 border-b border-neutral-100">
            <HqDetailElement
                :icon="Icons.TITaxi"
                :title="t('taxilib.receipt.taxi')">
                <template #value>
                    <div>
                        <p v-if="receipt.vehicle_registration">{{ receipt.vehicle_registration }}</p>
                        <p v-if="display_taxi_brand_color()">{{ display_taxi_brand_color() }}</p>
                    </div>
                </template>
            </HqDetailElement>
            <HqDetailElement
                :icon="Icons.TITaxi"
                :title="t('taxilib.receipt.side_number')"
                :value="receipt.side_number"
            />
            <HqDetailElement
                v-if="!props.ride"
                :icon="Icons.TITaxi"
                :title="t('taxilib.receipt.taxi_brand')"
                :value="receipt.taxi_brand"
            />
            <HqDetailElement
                v-if="!props.ride"
                :icon="Icons.TITaxi"
                :title="t('taxilib.receipt.taxi_color')"
                :value="receipt.taxi_color"
            />
            <HqDetailElement
                v-if="!props.ride"
                :icon="Icons.TISettings"
                :title="t('taxilib.receipt.taxi_vehicle_examination_to')"
                :value="receipt.taxi_vehicle_examination_to"
            />
        </div>
        <div>
            <div class="py-4 relative sm:hover:bg-neutral-50 transition-all duration-500 cursor-pointer" @click="company_open = !company_open">
                <HqDetailElement
                    :icon="Icons.TIHouse"
                    :title="t('taxilib.receipt.company_name')"
                    :value="receipt.company_name"
                />
                <div class="absolute right-0 top-1/2 -translate-y-1/2">
                    <Icons.TIExpandMore class="w-5 h-5 fill-neutral-500 transition-all duration-300" :class="{ 'rotate-180': company_open }"></Icons.TIExpandMore>
                </div>
            </div>
            <v-expand-transition>
                <div v-show="company_open" class="space-y-2 sm:space-y-3 pb-4">
                    <HqDetailElement
                        :icon="Icons.TINote"
                        :title="t('taxilib.receipt.company_vatid')"
                        :value="receipt.company_vatid"
                    />
                    <HqDetailElement
                        :icon="Icons.TIPOI"
                        :title="t('taxilib.receipt.company_address')">
                        <template #value>
                            <div>
                                <p v-if="receipt.company_city">{{ receipt.company_city }}</p>
                                <p v-if="display_company_postcode_address()">{{ display_company_postcode_address() }}</p>
                            </div>
                        </template>
                    </HqDetailElement>
                    <HqDetailElement
                        :icon="Icons.TIDocs"
                        :title="t('taxilib.receipt.taxi_company_license')"
                        :value="receipt.taxi_company_license"
                    />
                    <HqDetailElement
                        :icon="Icons.TIDocs"
                        :title="t('taxilib.receipt.taxi_company_license_issuing_authority')"
                        :value="receipt.taxi_company_license_issuing_authority"
                    />
                    <HqDetailElement
                        :icon="Icons.TIDocs"
                        :title="t('taxilib.receipt.taxi_company_license_extract')"
                        :value="receipt.taxi_company_license_extract"
                    />
                </div>
            </v-expand-transition>
        </div>
    </div>
</template>

<script setup>
    import { ref } from 'vue';
    import { display_utc_local, meters_to_kilometers, Icons, HqDetailElement } from 'taxi-lib';
    import { useI18n } from 'vue-i18n';
    
    const { t } = useI18n();
    const company_open = ref(false);

    const props = defineProps({
        receipt: Object,
        ride: {
            type: Boolean,
            default: false,
        }
    })

    function display_taxi_brand_color() {
        const parts = [
            props.receipt.taxi_brand,
            props.receipt.taxi_color
        ].filter(Boolean);
        return parts.length ? parts.join(' ') : null;
    }

    function display_company_postcode_address() {
        const parts = [
            props.receipt.company_postcode,
            props.receipt.company_address,
        ].filter(Boolean);
        return parts.length ? parts.join(', ') : null;
    }
</script>